<template>
  <button
    class="help-center-search__button"
    aria-label="Toggle the search dialog"
    v-bind="$attrs"
    @click="openSearch"
  >
    <HelpCenterIcon
      name="search"
      size-units="4"
    />
  </button>

  <HelpCenterModal
    :open="open"
    @close="closeSearch"
  >
    <template #head>
      <div class="help-center-search__header">
        <HelpCenterIcon
          :name="iconName"
          :class="iconClass"
          size-units="5"
          class="help-center-search__icon"
        />
        <HelpCenterSearchInput
          :focus="open"
          :request="request"
          @confirm="confirmWithProgress"
        />
        <button
          title="Close Dialog"
          class="help-center-search__close"
          @click="closeSearch"
        >
          <HelpCenterIcon
            name="closeCircle"
            size-units="4"
          />
        </button>
      </div>
    </template>
    <template #body>
      <div class="help-center-search__body">
        <HelpCenterSearchSuggestions
          v-if="showsSearchSuggestions"
          :search-suggestions="searchSuggestions"
          @close="closeSearch"
        />
        <HelpCenterSearchEmptyState
          v-else-if="showsEmptyState"
        />
        <HelpCenterSearchResults
          v-else
          :results="results"
          @close="closeSearch"
        />
      </div>

      <HelpCenterError :error="error" />
    </template>
  </HelpCenterModal>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import updateQuery from 'gleam-shared/network/updateQuery'
import useInProgress from 'gleam-shared/composables/useInProgress'
import { cancelRequest, dedupedRequest, ignoreCancelled } from 'gleam-shared/network/http'

import HelpCenterIcon from 'help-center/components/HelpCenterIcon'
import HelpCenterError from 'help-center/components/HelpCenterError'
import HelpCenterModal from 'help-center/components/HelpCenterModal'
import HelpCenterSearchInput from 'help-center/components/HelpCenterSearchInput'
import HelpCenterSearchResults from 'help-center/components/HelpCenterSearchResults'
import HelpCenterSearchEmptyState from 'help-center/components/HelpCenterSearchEmptyState'
import HelpCenterSearchSuggestions from 'help-center/components/HelpCenterSearchSuggestions'

const SEARCH_API = '/docs/search'

defineOptions({
  inheritAttrs: false
})

defineProps({
  searchSuggestions: {
    type: Array,
    required: true
  }
})
const open = ref(false)
const error = ref(null)
const request = ref(null)
const results = ref(null)
const [confirmInProgress, confirmWithProgress] = useInProgress(confirm)

const iconName = computed(() => {
  if (confirmInProgress.value) {
    return 'loader'
  } else {
    return 'search'
  }
})

const iconClass = computed(() => {
  if (confirmInProgress.value) {
    return 'help-center-search__icon--spin'
  } else {
    return null
  }
})

const showsEmptyState = computed(() => {
  return results.value.length === 0
})

const showsSearchSuggestions = computed(() => {
  return !results.value
})

watch(open, (value) => {
  if (value) {
    error.value = null
    request.value = null
    results.value = null
  } else {
    cancelRequest(SEARCH_API)
  }
})

function closeSearch() {
  open.value = false
}

function openSearch() {
  open.value = true
}

function confirm(input) {
  request.value = input

  if (!request.value) {
    cancelRequest(SEARCH_API)
    return
  }

  return dedupedRequest(
    updateQuery(SEARCH_API, { query: request.value }),
    SEARCH_API
  ).then((data) => {
    results.value = data
  }).catch(ignoreCancelled((err) => {
    error.value = err
  }))
}
</script>

<style lang="scss">
@import 'help-center/mixins';

.help-center-search {
  &__button {
    @include help-center-accent-link;
    display: flex;
    padding: 4px;
  }

  &__header {
    gap: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 12px 24px;
    border-bottom: 1px solid var(--help-center-border);
  }

  &__icon {
    color: var(--help-center-muted);

    &--spin {
      @include help-center-spin;
    }
  }

  &__close {
    @include help-center-muted-button;
    display: flex;
    height: fit-content;
  }

  &__body {
    padding: 16px 24px;
  }
}
</style>
